import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  completeAppraisal,
  fetchSavedQuestionAndAnswersSet
} from "../api/requests";
import { errorMessages } from "../data/filterTypes";
import CustomException from "../errors";
import { getAppraisalData } from "../store/selectors";
import {
  appraisalSlice,
  getAppraisal,
  getFileStackKeys,
  getQuestions,
  getSavedQuestionAndAnswersSet,
  getValuation,
  updateVehicle
} from "../store/slices/Appraisal";
import { useVehicle } from "./useVehicle";
import { useSourceSystem } from "./useSourceSystem";
import { sendEvent } from "../helpers/analytics";

export const useRetryRequest = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { fetchVehicle } = useVehicle();

  const {
    appraisalSessionId,
    questionsAndAnswers,
    vehicle
  } = useSelector(getAppraisalData);
  const {
    sourceSystem: { name, sendValuationRequestEvent }
  } = useSourceSystem();

  const [showSpinner, setShowSpinner] = useState(false);

  const retryUpdateVehicleRequest = (redirectUrl, mileage) =>
    dispatch(updateVehicle({ mileage, appraisalSessionId })).then(
      (response) => {
        if (response?.error) {
          retryRequest(() => retryUpdateVehicleRequest(redirectUrl, mileage));
        } else {
          dispatch(appraisalSlice.actions.setIsOnline(true));
          navigate(redirectUrl);
        }
      }
    );

  const retryRequest = (request) => {
    dispatch(appraisalSlice.actions.setIsOnline(false));
    setTimeout(() => {
      request();
    }, 3000);
  };

  const checkIfNetworkError = (response, request) => {
    if (response.error?.message === errorMessages.NETWORKERROR) {
      retryRequest(request);
    } else {
      dispatch(appraisalSlice.actions.setIsOnline(true));
    }
  };

  const questionsRequest = () =>
    dispatch(getQuestions(appraisalSessionId)).then((response) =>
      checkIfNetworkError(response, questionsRequest)
    );

  const appraisalRequest = () =>
    dispatch(getAppraisal(appraisalSessionId)).then((response) => {
      checkIfNetworkError(response, appraisalRequest);
    });

  const savedQuestionsAndAnswersRequest = () =>
    dispatch(getSavedQuestionAndAnswersSet(appraisalSessionId)).then(
      (response) => {
        checkIfNetworkError(response, fetchSavedQuestionAndAnswersSet);
      }
    );

  const vehicleAndQuestionsRequest = (regParam) => {
    fetchVehicle(regParam, appraisalSessionId).then((response) => {
      checkIfNetworkError(response, () => vehicleAndQuestionsRequest(regParam));
      if (!response.payload.error) {
        questionsRequest();
      }
    });
  };

  const fileStackRequest = () =>
    dispatch(getFileStackKeys(appraisalSessionId)).then((response) =>
      checkIfNetworkError(response, fileStackRequest)
    );

  const valuationRequest = async (valuationAccuracy) => {
    dispatch(appraisalSlice.actions.setIsLoading(true));
    dispatch(appraisalSlice.actions.setIsLoading(false));

    dispatch(
      getValuation({
        questionsAndAnswers,
        appraisalSessionId,
        valuationAccuracy
      })
    ).then((response) => {
      checkIfNetworkError(response, valuationRequest);
      if (!response.error) {
        if (sendValuationRequestEvent) {
          sendEvent("event", "app_logic", {
            label: `vehicleReg: ${vehicle?.registration}, 
            vehicleMake: ${vehicle?.manufacturer}, 
            vehicleModel: ${vehicle?.model}, 
            customerValuation: ${response.payload.result.valuation}, 
            sessionId: ${appraisalSessionId} 
            journey: ${name}`
          });
        }
      }
    });
  };

  const appraisalAndValuationRequest = (valuationAccuracy) =>
    dispatch(getAppraisal(appraisalSessionId)).then((response) => {
      checkIfNetworkError(response, appraisalAndValuationRequest);
      if (!response.error) {
        valuationRequest(valuationAccuracy);
      }
    });

  const submitCompleteAppraisal = async () => {
    setShowSpinner(true);
    await completeAppraisal(appraisalSessionId)
      .then(({ status, data }) => {
        if (status === 200) {
          window.location.href = data.result.redirectUri;
        } else {
          throw new CustomException(data.error);
        }
      })
      .catch((error) => {
        if (error.message === errorMessages.NETWORKERROR) {
          retryRequest(submitCompleteAppraisal);
        } else {
          if (!!error) {
            dispatch({ type: "SET_ERROR", data: error.code });
          }
          navigate("/error");
        }
      });
  };

  return {
    retryUpdateVehicleRequest,
    questionsRequest,
    appraisalRequest,
    vehicleAndQuestionsRequest,
    fileStackRequest,
    valuationRequest,
    appraisalAndValuationRequest,
    retryRequest,
    showSpinner,
    submitCompleteAppraisal,
    savedQuestionsAndAnswersRequest
  };
};
